import { useGetThemePerformance } from '~/react-query-hooks/themes/useGetThemePerformance';
import { useThemePerformance } from '~/react-query-hooks/themes/useThemeOverview';
import { formatNumber } from '~/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/number-formatter';

import { Horizon } from '../ThemePerformanceSection';

type UseThemePerformanceReturnProps = {
  entityTags: string[];
  benchmarkTags?: string[];
  numDays?: number; // Defaults to 1 year
};

export const getReturnValue = ({
  value,
  benchmarkValue,
  withBenchmark,
}: {
  value: number | undefined;
  benchmarkValue?: number;
  withBenchmark?: boolean;
}) => {
  if (withBenchmark) {
    if (benchmarkValue !== undefined && value !== undefined) {
      return value - benchmarkValue;
    } else {
      return undefined;
    }
  }

  return value;
};

export const useThemePerformanceReturn = ({
  entityTags,
  benchmarkTags,
  numDays,
}: UseThemePerformanceReturnProps) => {
  const { data, isFetching } = useThemePerformance(entityTags, numDays);
  const { data: benchmarkData, isFetching: isBenchmarkFetching } =
    useThemePerformance(benchmarkTags, numDays);
  const value = data?.aggregate?.at(-1)?.value;
  const benchmarkValue = benchmarkData?.aggregate?.at(-1)?.value;

  const returnValue = getReturnValue({
    value,
    benchmarkValue,
    withBenchmark: !!benchmarkTags?.length,
  });

  return {
    isLoading: isFetching || isBenchmarkFetching,
    value: formatNumber({ isPercentage: true, value: returnValue }),
    isNegative: !!returnValue && returnValue < 0,
  };
};

/**
 * @deprecated
 */
export const useRevampThemePerformanceReturn = ({
  themeId,
  selectedHorizon,
  benchmarkTags,
  numDays,
}: {
  themeId: string;
  selectedHorizon: Horizon;
  benchmarkTags?: string[];
  numDays?: number;
}) => {
  const { data, isFetching } = useGetThemePerformance({ themeId });
  const performanceData = data?.performance;

  const horizonReturn =
    performanceData?.[
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      selectedHorizon.toLowerCase() as keyof typeof performanceData
    ];

  const { data: benchmarkData, isFetching: isBenchmarkFetching } =
    useThemePerformance(benchmarkTags, numDays);
  const benchmarkValue = benchmarkData?.aggregate?.at(-1)?.value;

  const returnValue = getReturnValue({
    value: horizonReturn,
    benchmarkValue,
    withBenchmark: !!benchmarkTags?.length,
  });

  return {
    isLoading: isFetching || isBenchmarkFetching,
    value: formatNumber({ isPercentage: true, value: returnValue }),
    isNegative: !!returnValue && returnValue < 0,
  };
};
